/**
 * Tabs Component — THE tab bar for the whole app.
 *
 * The look lives in theme/tabs.ts as one stylesheet, not in this file. What
 * remains here is markup, the nesting-depth contract, and the measured
 * icon-only collapse — the parts that genuinely need JavaScript.
 *
 * THREE LEVELS, THREE SHAPES. A tab row renders differently depending on how
 * deeply it is nested, because the Account and Location modals stack up to
 * three rows directly on top of each other (Financials → Invoicing →
 * Schedules). When every row used the same chrome those three read as one
 * confusing seventeen-item control, and nothing told you which row was the
 * parent of which. See theme/tabs.ts for the shapes.
 *
 * DEPTH IS DERIVED, NOT DECLARED. `TabPanel` publishes its own depth + 1 on a
 * context, so a `Tabs` rendered inside a panel knows it is a sub-row without
 * being told. Twenty call sites therefore need no props, and a row cannot
 * drift out of sync with where it actually sits. Pass `level` only to override
 * for a row that is nested without an intervening TabPanel.
 *
 * Fit behavior: when every tab has an icon and the LABELED row would overflow
 * its container (phones), the row collapses to icon-only — labels come back as
 * soon as they fit again, and each button keeps its label as a tooltip/
 * aria-label. If icons alone still overflow (or tabs have no icons), the row
 * scrolls horizontally (hidden scrollbar, touch friendly) — consumers no
 * longer need their own scroll wrappers.
 *
 * Per-tab extras: `icon`, `badge` (green count chip), `locked` (lock glyph —
 * the surface is visible but read-only for this viewer), `testId` /
 * `lockTestId` for test hooks.
 */

import React, { useContext, useLayoutEffect, useRef, useState } from 'react';
import { injectTabStyles, tabClasses } from '../../theme/tabs';

export interface Tab {
  id: string;
  label: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  badge?: string;
  /** Lock glyph: surface is visible but read-only for this viewer. */
  locked?: boolean;
  /**
   * Attention glyph + amber label: this tab holds something the operator must
   * deal with before the form is valid (e.g. a new user with no access
   * assigned yet). Distinct from `badge`, which is a neutral count.
   */
  warning?: boolean;
  /** Tooltip for the warning glyph — say what is wrong, not that it is wrong. */
  warningTitle?: string;
  /** data-testid for the tab button. */
  testId?: string;
  /** data-testid for the lock glyph (defaults to `${testId}-lock`). */
  lockTestId?: string;
}

export interface TabsProps {
  tabs: Tab[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  /** Metrics for the top-level row; nested rows size themselves by depth. */
  size?: 'small' | 'medium' | 'large';
  fullWidth?: boolean;
  /** aria-label for the tablist. */
  ariaLabel?: string;
  /**
   * Nesting level override. Normally omitted — depth comes from the enclosing
   * TabPanel. Set it only for a sub-row that is not wrapped in one.
   */
  level?: number;
}

/**
 * How many TabPanels enclose this point in the tree. Read by Tabs to pick its
 * shape, incremented by TabPanel.
 */
const TabsDepthContext = React.createContext(0);

/** Deepest level with its own shape; anything below keeps the quietest one. */
const MAX_DEPTH = 2;

/**
 * Declares that everything inside sits one tab level deeper.
 *
 * `TabPanel` does this automatically, so most callers never need it. It exists
 * for the containers that render their panels by hand — `{activeTab === 'x' &&
 * <Panel/>}` with their own visited-tab bookkeeping — where there is no
 * TabPanel to carry the depth (UserModal, ConnectionDetail). Without it every
 * sub-row inside such a container would render as a full top-level tab strip.
 *
 * Renders no DOM of its own, so it is safe to wrap an existing layout in.
 */
export const TabsLevel: React.FC<{ children: React.ReactNode; value?: number }> = ({ children, value }) => {
  const depth = useContext(TabsDepthContext);
  return <TabsDepthContext.Provider value={value ?? depth + 1}>{children}</TabsDepthContext.Provider>;
};

// Lock glyph drawn inline so @turn-connect/ui keeps zero icon deps.
const LockGlyph: React.FC<{ testId?: string }> = ({ testId }) => (
  <svg
    width={11}
    height={11}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2.5}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-label="Read only"
    data-testid={testId}
    style={{ flexShrink: 0, color: 'currentColor', opacity: 0.75 }}
  >
    <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

/**
 * Inside a TabsKeepLabels region every tab row keeps its text labels and
 * wraps onto more lines when it does not fit (the design system's segmented
 * tabs, see theme/tabs.ts), instead of collapsing to icons.
 * Settings pages use it: an icon alone does not say what a tab holds.
 */
const TabsKeepLabelsContext = React.createContext(false);

export const TabsKeepLabels: React.FC<{ children: React.ReactNode; value?: boolean }> = ({ children, value = true }) => (
  <TabsKeepLabelsContext.Provider value={value}>{children}</TabsKeepLabelsContext.Provider>
);

/**
 * True inside a TabsKeepLabels region (the settings pages, not their dialogs).
 * Other tab controls, such as the web ds SegmentedTabs, read it to draw the
 * settings pages' quieter chosen tab (`chosenTabLook`) instead of a solid fill.
 */
export const useInTabsKeepLabels = (): boolean => useContext(TabsKeepLabelsContext);

export const Tabs: React.FC<TabsProps> = ({ tabs, activeTab, onTabChange, size = 'medium', fullWidth = false, ariaLabel, level }) => {
  injectTabStyles();

  const contextDepth = useContext(TabsDepthContext);
  const depth = Math.min(level ?? contextDepth, MAX_DEPTH);

  // Icon-only collapse: only possible when every tab has an icon (otherwise
  // buttons would go blank). Measured, not breakpoint-based — a two-tab row
  // keeps labels on a phone while a nine-tab row collapses.
  const rowRef = useRef<HTMLDivElement>(null);
  const labeledWidthRef = useRef(0);
  const [iconOnly, setIconOnly] = useState(false);
  const keepLabels = useContext(TabsKeepLabelsContext);
  const collapsible = !keepLabels && tabs.length > 0 && tabs.every((t) => !!t.icon);

  useLayoutEffect(() => {
    const el = rowRef.current;
    if (!el || !collapsible) {
      setIconOnly(false);
      return;
    }
    // The effect re-runs whenever iconOnly flips, so the closure always sees
    // the mode it is measuring.
    const check = () => {
      const width = el.clientWidth;
      if (width === 0) return; // hidden / jsdom — keep labels
      if (!iconOnly) {
        // Labels are rendered: remember their natural width, collapse on overflow.
        labeledWidthRef.current = el.scrollWidth;
        if (el.scrollWidth > width + 1) setIconOnly(true);
      } else if (labeledWidthRef.current > 0 && width >= labeledWidthRef.current) {
        // Container grew back past the remembered labeled width: restore labels.
        setIconOnly(false);
      }
    };
    check();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [collapsible, iconOnly, tabs]);

  const showLabels = !iconOnly;

  return (
    <div className={tabClasses.wrapper} data-depth={depth} style={{ width: fullWidth ? '100%' : 'auto' }}>
      <div
        ref={rowRef}
        className={tabClasses.row}
        role="tablist"
        aria-label={ariaLabel}
        data-depth={depth}
        data-size={size}
        data-full-width={fullWidth || undefined}
        // Settings pages (TabsKeepLabels) draw the design system's segmented tabs (theme/tabs.ts).
        data-look={keepLabels ? 'segmented' : undefined}
        style={{ width: fullWidth ? '100%' : 'auto' }}
      >
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={tabClasses.tab}
            onClick={() => !tab.disabled && onTabChange(tab.id)}
            disabled={tab.disabled}
            aria-selected={activeTab === tab.id}
            role="tab"
            data-testid={tab.testId}
            data-warning={tab.warning || undefined}
            title={tab.label || undefined}
            aria-label={tab.label || undefined}
          >
            {tab.icon && <span style={{ display: 'flex', alignItems: 'center' }}>{tab.icon}</span>}
            {showLabels && tab.label}
            {tab.locked && <LockGlyph testId={tab.lockTestId ?? (tab.testId ? `${tab.testId}-lock` : undefined)} />}
            {tab.warning && (
              <span className={tabClasses.warning} title={tab.warningTitle} aria-label={tab.warningTitle ?? 'Needs attention'}>
                ⚠
              </span>
            )}
            {tab.badge && <span className={tabClasses.badge}>{tab.badge}</span>}
          </button>
        ))}
      </div>
    </div>
  );
};

/**
 * TabPanel Component
 * Container for tab content with optional lazy loading.
 *
 * Also publishes its nesting depth, so a Tabs rendered inside it picks the
 * sub-row shape without the call site passing anything.
 */
export interface TabPanelProps {
  children: React.ReactNode;
  tabId: string;
  activeTab: string;
  lazy?: boolean;
  keepMounted?: boolean;
}

export const TabPanel: React.FC<TabPanelProps> = ({ children, tabId, activeTab, lazy = false, keepMounted = false }) => {
  const isActive = activeTab === tabId;
  const depth = useContext(TabsDepthContext);
  const [hasBeenActive, setHasBeenActive] = React.useState(isActive);

  React.useEffect(() => {
    if (isActive && !hasBeenActive) {
      setHasBeenActive(true);
    }
  }, [isActive, hasBeenActive]);

  if (lazy && !hasBeenActive) {
    return null;
  }

  if (!keepMounted && !isActive) {
    return null;
  }

  return (
    <TabsDepthContext.Provider value={depth + 1}>
      <div
        role="tabpanel"
        aria-hidden={!isActive}
        style={{
          display: isActive ? 'block' : 'none',
        }}
      >
        {children}
      </div>
    </TabsDepthContext.Provider>
  );
};
